import { useEffect, useState } from 'react';
import { db } from '../db/indexedDb';
import { buildArtworkDescriptor, createObjectUrl, revokeObjectUrl } from '../lib/audio';
import { cn } from '../lib/cn';
import type { Track } from '../types';

interface ArtworkProps {
  track?: Track;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizeMap = {
  sm: 'h-12 w-12',
  md: 'h-16 w-16',
  lg: 'h-32 w-32',
  xl: 'h-72 w-72'
};

export function Artwork({ track, size = 'md', className }: ArtworkProps) {
  const [src, setSrc] = useState<string>();

  useEffect(() => {
    let active = true;
    let url: string | undefined;

    void (async () => {
      if (!track?.artworkBlobId) {
        setSrc(undefined);
        return;
      }
      const blobDoc = await db.getBlob(track.artworkBlobId);
      url = createObjectUrl(blobDoc);
      if (active) setSrc(url);
    })();

    return () => {
      active = false;
      revokeObjectUrl(url);
    };
  }, [track?.artworkBlobId]);

  if (!track) {
    return <div className={cn('rounded-3xl bg-white/5', sizeMap[size], className)} />;
  }

  const placeholder = buildArtworkDescriptor(track);

  return src ? (
    <img
      src={src}
      alt={`${track.album} artwork`}
      className={cn('rounded-3xl object-cover shadow-glow', sizeMap[size], className)}
      loading="lazy"
    />
  ) : (
    <div
      aria-label={`${track.title} placeholder artwork`}
      className={cn(
        'flex items-center justify-center rounded-3xl text-2xl font-display font-bold text-white shadow-glow',
        sizeMap[size],
        className
      )}
      style={{ backgroundImage: placeholder.gradient }}
    >
      {placeholder.label}
    </div>
  );
}
